import { SecuredLayout } from '../Layout/SecuredLayout';
import { Box, Breadcrumbs, Grid, Link as MuiLink, Tab, Tabs } from '@mui/material';
import { StyledCard } from '../Layout/styles';
import SendNotification from './SendNotification';
import { PartnerWorkerDeviceDetails, WorkerDevice, WorkerDeviceDetailsByTravel } from '../../@types/employeeData';
import React, { useEffect, useState } from 'react';
import { useAuth, useProvideSnackBar } from '../../hooks';
import { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { getEmployeeDevicesByCompany } from '../../service/workplace-service';
import { Link } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import WorkerDeviceDataGrid from './WorkerDeviceDataGrid';
import WorkplaceDataGrid from './WorkplaceDataGrid';
import LineDataGrid from './LineDataGrid';
import { TAB_VIEW } from '../../@types';

const WORKER_TRANSPORT_API_URL = (window as any).WORKER_TRANSPORT_API_URL;

export type WorkerDeviceDataGridProps = {
    loading: boolean
    workerDeviceDetailsByTravels: WorkerDeviceDetailsByTravel[],
    rowSelectionModel: GridRowId[],
    setRowSelectionModel: (value: GridRowId[]) => void
}

export type PartnerDataGridProps = {
    loading: boolean
    partnerDeviceDetails: Record<string, WorkerDevice[]>,
    rowSelectionModel: GridRowId[],
    setRowSelectionModel: (value: GridRowId[]) => void
}

export default function WorkerTravelDetailsPage() {
    const { user, hasInsiderRoles } = useAuth();
    const { showError, showResponseError } = useProvideSnackBar();
    const [workerDeviceDetailsByTravels, setWorkerDeviceDetailsByTravels] = useState<WorkerDeviceDetailsByTravel[]>([]);
    const [devicesByCompany, setDevicesByCompany] = useState<Record<string, PartnerWorkerDeviceDetails[]>>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>([]);
    const [viewTab, setViewTab] = useState<TAB_VIEW>(TAB_VIEW.DEVICE);

    const fetchWorkerDetailsSignedInIntoBuszomApp = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${WORKER_TRANSPORT_API_URL}/v1/app-user/`, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user?.accessToken}`,
                },
            });
            if (!response.ok) {
                await showResponseError(response);
                setLoading(false);
                return;
            }
            const existingDetails: WorkerDeviceDetailsByTravel[] = await response.json();
            setWorkerDeviceDetailsByTravels(existingDetails);
        } catch (error: any) {
            showError('Hiba a szerver elérésében! ' + error.message);
        }
        setLoading(false);
    };

    useEffect(() => {
        if ((viewTab === TAB_VIEW.DEVICE || viewTab === TAB_VIEW.LINE) && workerDeviceDetailsByTravels.length === 0) {
            fetchWorkerDetailsSignedInIntoBuszomApp();
            return;
        }
        if (viewTab === TAB_VIEW.WORKPLACE && Object.keys(devicesByCompany).length === 0) {
            setLoading(true);

            getEmployeeDevicesByCompany('',
                user?.accessToken || '',
                (response) => showResponseError(response),
                (message) => showError(message))
                .then((data) => data ? setDevicesByCompany(data) : null)
                .finally(() => setLoading(false));
        }
    }, [viewTab]);

    return (
        <SecuredLayout>
            <Grid sx={{ position: 'relative', zIndex: 10, textAlign: 'right' }}>
                {hasInsiderRoles && (
                    <Breadcrumbs aria-label="breadcrumb" sx={{ display: 'inline-block' }}>
                        <MuiLink
                            component={Link}
                            underline="hover"
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', textAlign: 'right' }}
                            px={{ xs: 1, md: 2 }}
                            color="inherit"
                            to="/hr">
                            Váltás a HR nézetre
                            <PersonIcon sx={{ ml: 0.5, mb: -0.125 }} fontSize={'small'} />
                        </MuiLink>
                    </Breadcrumbs>
                )}
            </Grid>
            <Grid display={'flex'} flexDirection={'column'} px={{ xs: 1, md: 2 }} pt={1}
                  sx={{ justifyContent: 'center'}}>
                <Grid container display={'flex'} spacing={4} justifyContent={'center'}>
                    <Grid display={'flex'} item xs={12} md={5} lg={4}>
                        <SendNotification
                            workerTravelDevices={workerDeviceDetailsByTravels}
                            devicesByCompany={devicesByCompany}
                            devicesByWorkerLanguageKey={null}
                            onSendSuccess={() => setRowSelectionModel([])}
                            viewTab={viewTab}
                            rowSelectionModel={rowSelectionModel}
                        />
                    </Grid>
                    <Grid item xs={12} display={'flex'}>
                        <StyledCard>
                            <Box>
                                <Tabs value={viewTab} onChange={(event, tabValue) => setViewTab(tabValue)}>
                                    <Tab value={TAB_VIEW.DEVICE} label={'Utaslista'}></Tab>
                                    <Tab value={TAB_VIEW.LINE} label={'Járatlista'}></Tab>
                                    <Tab value={TAB_VIEW.WORKPLACE} label={'Partnerlista'}></Tab>
                                </Tabs>
                                {viewTab === TAB_VIEW.DEVICE && <WorkerDeviceDataGrid
                                    loading={loading}
                                    workerDeviceDetailsByTravels={workerDeviceDetailsByTravels}
                                    rowSelectionModel={rowSelectionModel}
                                    setRowSelectionModel={setRowSelectionModel} />}

                                {viewTab === TAB_VIEW.LINE && <LineDataGrid
                                    loading={loading}
                                    workerDeviceDetailsByTravels={workerDeviceDetailsByTravels}
                                    rowSelectionModel={rowSelectionModel}
                                    setRowSelectionModel={setRowSelectionModel} />}

                                {viewTab === TAB_VIEW.WORKPLACE && <WorkplaceDataGrid
                                    loading={loading}
                                    partnerDeviceDetails={devicesByCompany}
                                    rowSelectionModel={rowSelectionModel}
                                    setRowSelectionModel={setRowSelectionModel} />}
                            </Box>
                        </StyledCard>
                    </Grid>
                </Grid>
            </Grid>
        </SecuredLayout>
    )
        ;
}
